import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { authAtom } from '@/atom/authAtom';
import { useLoginRequest, useVerifyOtp } from '@/hooks/auth.hook';
import { errorToast, successToast } from '@/common/toast';
import { ErrorMessage, Input } from '@/components/common';
import { passwordValidation } from '@/util/common';
import axios from 'axios';
import { OTP_VALID_SECONDS } from '@/constants/users';

const formatOtpCountdown = (totalSeconds) => {
  const m = Math.floor(Math.max(0, totalSeconds) / 60);
  const s = Math.max(0, totalSeconds) % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const Copyright = ({ sx }) => {
  return (
    <Typography variant='body2' color='text.secondary' align='center' sx={sx}>
      {'Copyright © '}
      <Link color='inherit' href='https://secolab.kr/' target='_blank' rel='noopener noreferrer'>
        openeg
      </Link>{' '}
      {new Date().getFullYear()}.
    </Typography>
  );
};

const getApiErrorMessage = (error, fallback) => {
  if (axios.isAxiosError(error)) {
    return (
      error.response?.data?.message ||
      error.response?.data?.msg ||
      error.message ||
      fallback
    );
  }
  return error?.message || fallback;
};

const LoginPage = () => {
  const navigate = useNavigate();
  const auth = useAtomValue(authAtom);
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [preAuthToken, setPreAuthToken] = useState('');
  const [otpRemainingSeconds, setOtpRemainingSeconds] = useState(OTP_VALID_SECONDS);
  const otpExpiryToastShownRef = useRef(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      username: '',
      password: '',
      otpCode: '',
    },
  });

  const loginRequestMutation = useLoginRequest();
  const verifyOtpMutation = useVerifyOtp();

  // 이미 로그인된 경우 홈으로 리다이렉트
  useEffect(() => {
    if (auth && auth.token) {
      navigate('/');
    }
  }, [auth, navigate]);

  // OTP 단계: 5분 카운트다운
  useEffect(() => {
    if (step !== 'otp') {
      otpExpiryToastShownRef.current = false;
      return;
    }
    setOtpRemainingSeconds(OTP_VALID_SECONDS);
    const id = setInterval(() => {
      setOtpRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [step]);

  useEffect(() => {
    if (step !== 'otp' || otpRemainingSeconds > 0 || otpExpiryToastShownRef.current) return;
    otpExpiryToastShownRef.current = true;
    errorToast('인증 시간이 만료되었습니다. 처음부터 다시 로그인해주세요.');
  }, [step, otpRemainingSeconds]);

  const onCredentialsSubmit = (data) => {
    loginRequestMutation.mutate(
      { username: data.username, password: data.password },
      {
        onSuccess: (result) => {
          setPreAuthToken(result.preAuthToken);
          setStep('otp');
          reset({ ...data, otpCode: '' });
          if (result.message) {
            successToast(result.message);
          }
        },
        onError: (error) => {
          if (axios.isAxiosError(error)) {
            const { response } = error;
            const errorMessage = getApiErrorMessage(error, '로그인에 실패했습니다.');

            if (response?.status === 401) {
              errorToast(errorMessage);
            } else if (response?.status === 400) {
              errorToast(errorMessage);
            } else if (response?.status === 500) {
              errorToast('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
            } else if (!response) {
              errorToast('네트워크 오류가 발생했습니다. 연결을 확인해주세요.');
            } else {
              errorToast(errorMessage);
            }
          } else {
            errorToast(getApiErrorMessage(error, '로그인 처리 중 오류가 발생했습니다.'));
          }
        },
      }
    );
  };

  const onOtpSubmit = (data) => {
    if (otpRemainingSeconds <= 0) {
      errorToast('인증 시간이 만료되었습니다. 처음부터 다시 로그인해주세요.');
      return;
    }
    const otpCode = String(data.otpCode || '').replace(/\s/g, '');
    verifyOtpMutation.mutate(
      { preAuthToken, otpCode },
      {
        onSuccess: () => {
          navigate('/');
        },
        onError: (error) => {
          if (axios.isAxiosError(error)) {
            const { response } = error;
            const status = response?.status;

            if (status === 429) {
              errorToast('시도 횟수를 초과했습니다. 처음부터 다시 로그인해주세요.');
              setStep('credentials');
              setPreAuthToken('');
              reset({ username: '', password: '', otpCode: '' });
              return;
            }

            const errorMessage = getApiErrorMessage(error, 'OTP 인증에 실패했습니다.');
            if (status === 401) {
              errorToast(errorMessage);
            } else if (status === 400) {
              errorToast(errorMessage);
            } else if (status === 500) {
              errorToast('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
            } else if (!response) {
              errorToast('네트워크 오류가 발생했습니다. 연결을 확인해주세요.');
            } else {
              errorToast(errorMessage);
            }
          } else {
            errorToast(getApiErrorMessage(error, 'OTP 인증 처리 중 오류가 발생했습니다.'));
          }
        },
      }
    );
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setPreAuthToken('');
    reset((values) => ({ ...values, otpCode: '' }));
  };

  const isCredentialsStep = step === 'credentials';
  const isPending = isCredentialsStep ? loginRequestMutation.isPending : verifyOtpMutation.isPending;
  const isOtpExpired = step === 'otp' && otpRemainingSeconds <= 0;

  return (
    <Container component='div' maxWidth='xs'>
      <Box
        sx={{
          marginTop: 25,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box sx={{ mb: 5 }}>
          <img src='/logo-secolab.png' alt='secolab logo' />
        </Box>
        <Typography component='h1' variant='h5'>
          시코랩 관리자 로그인
        </Typography>

        {isCredentialsStep ? (
          <Box component='form' onSubmit={handleSubmit(onCredentialsSubmit)} noValidate sx={{ mt: 1, width: '100%' }}>
            <Input
              textFieldProps={{
                fullWidth: true,
                autoFocus: true,
                margin: 'normal',
                label: 'ID',
                autoComplete: 'username',
              }}
              name='username'
              control={control}
              rules={{
                required: 'ID를 입력해주세요.',
                maxLength: {
                  value: 20,
                  message: '최대 20글자까지만 허용됩니다.',
                },
              }}
            />
            {errors.username && (
              <ErrorMessage
                text={
                  typeof errors.username.message === 'string'
                    ? errors.username.message
                    : 'ID를 입력해주세요.'
                }
              />
            )}

            <Input
              textFieldProps={{
                fullWidth: true,
                margin: 'normal',
                label: '비밀번호',
                autoComplete: 'current-password',
                type: 'password',
              }}
              name='password'
              control={control}
              rules={{
                validate: passwordValidation,
                required: '비밀번호를 입력해주세요.',
                minLength: {
                  value: 6,
                  message: '최소 6글자에서 20글자 사이만 허용됩니다.',
                },
                maxLength: {
                  value: 20,
                  message: '최소 6글자에서 20글자 사이만 허용됩니다.',
                },
              }}
            />
            {errors.password && (
              <ErrorMessage
                text={
                  typeof errors.password.message === 'string'
                    ? errors.password.message
                    : '비밀번호를 확인해주세요.'
                }
              />
            )}

            <Button
              type='submit'
              fullWidth
              variant='contained'
              disabled={isPending}
              sx={{ mt: 3, mb: 2 }}
            >
              {isPending ? <CircularProgress size={24} color='inherit' /> : '로그인 (OTP 발송)'}
            </Button>
          </Box>
        ) : (
          <Box component='form' onSubmit={handleSubmit(onOtpSubmit)} noValidate sx={{ mt: 1, width: '100%' }}>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              이메일로 발송된 6자리 인증 코드를 입력해주세요. (최대 5회 시도)
            </Typography>
            <Typography
              variant='subtitle2'
              component='div'
              sx={{
                mb: 2,
                fontWeight: 600,
                color: isOtpExpired ? 'error.main' : 'primary.main',
              }}
            >
              남은 시간 {formatOtpCountdown(otpRemainingSeconds)}
            </Typography>
            {isOtpExpired && (
              <Typography variant='body2' color='error' sx={{ mb: 2 }}>
                유효 시간이 지났습니다. 아래 버튼으로 처음 화면으로 돌아가 다시 로그인해주세요.
              </Typography>
            )}
            <Input
              textFieldProps={{
                fullWidth: true,
                autoFocus: true,
                margin: 'normal',
                label: 'OTP 코드',
                autoComplete: 'one-time-code',
                disabled: isOtpExpired,
                inputProps: {
                  inputMode: 'numeric',
                  maxLength: 6,
                },
              }}
              name='otpCode'
              control={control}
              rules={{
                required: 'OTP 코드를 입력해주세요.',
                pattern: {
                  value: /^[0-9]{6}$/,
                  message: '6자리 숫자를 입력해주세요.',
                },
              }}
            />
            {errors.otpCode && (
              <ErrorMessage
                text={
                  typeof errors.otpCode.message === 'string'
                    ? errors.otpCode.message
                    : 'OTP 코드를 확인해주세요.'
                }
              />
            )}

            <Button
              type='submit'
              fullWidth
              variant='contained'
              disabled={isPending || isOtpExpired}
              sx={{ mt: 3, mb: 1 }}
            >
              {isPending ? <CircularProgress size={24} color='inherit' /> : '인증하고 로그인'}
            </Button>
            <Button type='button' fullWidth variant='text' onClick={handleBackToCredentials} disabled={isPending}>
              처음으로 (아이디·비밀번호 다시 입력)
            </Button>
          </Box>
        )}
      </Box>
      <Copyright sx={{ mt: 8, mb: 4 }} />
    </Container>
  );
};

export default LoginPage;
